import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import UserModal from './UserModal';
import UserViewModal from './UserViewModal';
import ConfirmModal from './ConfirmModal';
import { apiClient } from '../config/api';
import { checkToken } from '../utils/debug';
import { showSuccess, showError } from '../utils/toast';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const lastFetchedPage = useRef(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'OK',
    cancelText: 'Cancel',
    confirmButtonType: 'danger'
  });

  // Safety check: ensure users is always an array
  const safeUsers = Array.isArray(users) ? users : [];

  // Debug: Log users state
  useEffect(() => {
    console.log('🔍 Users state updated:', safeUsers.length, 'users');
    if (safeUsers.length > 0) {
      console.log('📋 Sample user:', safeUsers[0]);
    }
  }, [users]);


  useEffect(() => {
    // Reset the ref if page changed (allow fetch for new page)
    if (lastFetchedPage.current !== null && lastFetchedPage.current !== currentPage) {
      lastFetchedPage.current = null;
    }

    // Prevent double call in React StrictMode for the same page
    if (lastFetchedPage.current === currentPage) {
      return;
    }

    // Mark this page as fetched
    lastFetchedPage.current = currentPage;

    // Debug: Check token on component mount
    checkToken();
    fetchUsers();
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Debug: Check if token exists
      if (!token) {
        alert('No token found in localStorage. Please login again.');
        return;
      }

      // Build query parameters
      let queryParams = `page=${currentPage}&page_size=${pageSize}`;

      console.log('Fetching users with token:', token.substring(0, 20) + '...');
      console.log('API URL:', `https://sayagainservices.sayagain.ai/api/admin/users?${queryParams}`);

      // API Call: GET /admin/users?page=1&page_size=20&search=...&plan=...&has_avatar=...&status=...
      // Headers: Authorization: Bearer YOUR_ADMIN_ACCESS_TOKEN
      const response = await apiClient.get(
        `/admin/users?${queryParams}`,
        token
      );

      console.log('API Response:', response);
      console.log('Response structure:', {
        hasData: !!response.data,
        hasUsers: !!response.data?.users,
        isArray: Array.isArray(response),
        status: response.status,
        statusCode: response.status_code
      });

      // Handle the actual API response structure:
      // { status: true, status_code: 200, data: { users: [...], total: 18, page: 1, page_size: 20 }, error: null, msg: "..." }
      let usersList = [];
      let totalPagesCount = 1;
      let totalUsersCount = 0;

      if (response && response.data && Array.isArray(response.data.users)) {
        // Correct structure: response.data.users
        usersList = response.data.users;
        totalUsersCount = response.data.total || usersList.length;
        const responsePageSize = response.data.page_size || pageSize;
        totalPagesCount = Math.ceil(totalUsersCount / responsePageSize) || 1;
        console.log('✅ Found users in response.data.users, count:', usersList.length, 'total:', totalUsersCount);
      } else if (Array.isArray(response)) {
        // Response is directly an array
        usersList = response;
        totalUsersCount = response.length;
        console.log('✅ Response is array, users count:', usersList.length);
      } else if (response && Array.isArray(response.data)) {
        // Response has data property that is an array
        usersList = response.data;
        totalPagesCount = response.total_pages || response.totalPages || response.pages || 1;
        totalUsersCount = response.total || response.totalUsers || response.count || usersList.length;
        console.log('✅ Response has data array, users count:', usersList.length);
      } else if (response && response.users && Array.isArray(response.users)) {
        // Response has users property
        usersList = response.users;
        totalPagesCount = response.total_pages || response.totalPages || 1;
        totalUsersCount = response.total || usersList.length;
        console.log('✅ Response has users property, users count:', usersList.length);
      } else {
        // Fallback: try to extract any array from response
        console.warn('⚠️ Unexpected response structure:', response);
        usersList = [];
      }

      // Ensure usersList is always an array
      if (!Array.isArray(usersList)) {
        console.error('❌ usersList is not an array:', usersList);
        usersList = [];
      }

      // Normalize user status from API response
      // API may return "inactive" but we need to ensure status is properly set
      usersList = usersList.map(user => {
        // Extract status from various possible locations in the response
        let userStatus = String(user.status || user.plan_details?.current?.status || 'active').toLowerCase();

        // Normalize: API might return "inactive" but we want to preserve it
        // Don't convert "inactive" to "deactive" here - keep it as is for consistency
        // The display logic will handle the normalization

        // Ensure status is always set on the user object
        return {
          ...user,
          status: userStatus // Ensure status is always present
        };
      });

      console.log('📊 Setting users state with', usersList.length, 'users');
      console.log('📋 First user sample:', usersList[0]);
      console.log('📋 First user status:', usersList[0]?.status);

      // Ensure we're setting an array - use spread to create new array reference
      if (Array.isArray(usersList)) {
        setUsers([...usersList]); // Create new array reference to trigger re-render
        setTotalPages(totalPagesCount);
        setTotalUsers(totalUsersCount);
        console.log('✅ Users state updated successfully with', usersList.length, 'users');
      } else {
        console.warn('⚠️ usersList is not an array:', usersList);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      const errorMessage = error.message || error.error || 'Unknown error';
      const errorStatus = error.status ? ` (Status: ${error.status})` : '';

      // If token error, redirect to login
      if (error.status === 401 || error.status === 403) {
        alert('Your session has expired. Please login again.');
        window.location.href = '/login';
        return;
      }

      alert(`Failed to fetch users: ${errorMessage}${errorStatus}\n\nPlease check:\n1. Token is stored in localStorage\n2. API server is accessible at https://sayagainservices.sayagain.ai/api\n3. CORS is enabled on the API server`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonType: 'danger',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          const token = localStorage.getItem('token');
          await apiClient.delete(`/admin/users/${id}`, token);

          // Remove user from local state without refreshing the page
          setUsers(prevUsers => prevUsers.filter(user => String(user.id || user.user_id) !== String(id)));

          // Update total users count
          setTotalUsers(prevTotal => {
            const newTotal = Math.max(0, prevTotal - 1);
            // Recalculate total pages based on the new total
            setTotalPages(Math.max(1, Math.ceil(newTotal / pageSize)));
            return newTotal;
          });

          // Check if we need to go to previous page (if we deleted the last item on current page)
          if (users.length === 1 && currentPage > 1) {
            setCurrentPage(prevPage => prevPage - 1);
            lastFetchedPage.current = null;
          }

          showSuccess('User deleted successfully');
        } catch (error) {
          console.error('Error deleting user:', error);
          showError('Failed to delete user: ' + (error.error || error.message || 'Unknown error'));
        }
      }
    });
  };

  const handleStatusToggle = (userId, currentStatus) => {
    // Normalize status: handle "inactive" as "deactive"
    const statusLower = String(currentStatus || 'active').toLowerCase();
    const normalizedStatus = (statusLower === 'inactive' || statusLower === 'deactive') ? 'deactive' : 'active';
    const action = normalizedStatus === 'active' ? 'deactivate' : 'activate';

    setConfirmModal({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} this user?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancel',
      confirmButtonType: 'primary',
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            alert('No authentication token found. Please login again.');
            window.location.href = '/login';
            return;
          }

          // API expects "active" or "deactive"
          const newStatus = normalizedStatus === 'active' ? 'deactive' : 'active';

          // API endpoint: POST /api/admin/users/{id}/status?status=active or status=deactive
          const endpoint = `/admin/users/${userId}/status?status=${newStatus}`;
          console.log(`Updating user ${userId} status from ${currentStatus} to ${newStatus}`);
          console.log(`API endpoint: ${endpoint}`);

          const response = await apiClient.post(endpoint, {}, token);
          console.log('Status update response:', response);

          // Update local state immediately without calling API again
          // Try to get status from API response first, fallback to our expected status
          let updatedStatus = newStatus === 'deactive' ? 'inactive' : 'active';

          // Check if response contains status information
          if (response && response.data) {
            if (response.data.status) {
              updatedStatus = response.data.status;
            } else if (response.data.user && response.data.user.status) {
              updatedStatus = response.data.user.status;
            }
          } else if (response && response.status && typeof response.status === 'string') {
            updatedStatus = response.status;
          } else if (response && response.user && response.user.status) {
            updatedStatus = response.user.status;
          }

          updatedStatus = String(updatedStatus).toLowerCase();

          setUsers(prevUsers => {
            return prevUsers.map(user => {
              if ((user.id || user.user_id) === userId) {
                return {
                  ...user,
                  status: updatedStatus
                };
              }
              return user;
            });
          });

          console.log('✅ User status updated locally without API refresh. New status:', updatedStatus);

          // Show success toast
          const actionText = updatedStatus === 'active' ? 'activated' : 'deactivated';
          showSuccess(`User ${actionText} successfully`);

        } catch (error) {
          console.error('Error updating user status:', error);
          console.error('Error details:', {
            message: error.message,
            error: error.error,
            status: error.status,
            data: error.data
          });

          // On error, refresh to get correct state from server
          lastFetchedPage.current = null;
          await fetchUsers();

          // Show detailed error message
          let errorMessage = 'Failed to update user status';
          if (error.error) {
            errorMessage += ': ' + error.error;
          } else if (error.message) {
            errorMessage += ': ' + error.message;
          } else if (error.data) {
            // Handle API error response structure
            if (typeof error.data === 'string') {
              errorMessage += ': ' + error.data;
            } else if (error.data.message) {
              errorMessage += ': ' + error.data.message;
            } else if (error.data.error) {
              errorMessage += ': ' + error.data.error;
            }
          }

          showError(errorMessage);
        }
      }
    });
  };

  const handleModalClose = (shouldRefresh = false) => {
    setShowModal(false);
    setEditingUser(null);
    if (shouldRefresh) {
      lastFetchedPage.current = null;
      fetchUsers();
    }
  };

  const handleView = (user) => {
    setViewingUser(user);
    setShowViewModal(true);
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setViewingUser(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };


  if (loading) {
    return (
      <Layout>
        <div className="users-loading">Loading users...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="users-page">
        {/* Header Section */}
        <div className="users-page-header">
          <div className="header-left">
            <h1 className="page-title">Users</h1>
          </div>
          <div className="filter-tabs-right">
            {/* Filters and Menu buttons hidden per request */}
          </div>
        </div>

        {/* Table Container */}
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Date Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {safeUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-users">
                    <div className="empty-state">
                      {loading ? 'Loading...' : 'No users found'}
                    </div>
                  </td>
                </tr>
              ) : (
                safeUsers.map((user, index) => {
                  if (index === 0) {
                    console.log('🎨 Rendering user data:', user);
                  }
                  const userStatus = String(user.status || user.plan_details?.current?.status || 'active').toLowerCase();
                  const displayStatus = userStatus === 'inactive' ? 'deactive' : userStatus;
                  const isActive = displayStatus === 'active';
                  const rowNumber = (currentPage - 1) * pageSize + index + 1;

                  return (
                    <tr key={user.id || user.user_id || `user-${index}`}>
                      <td className="row-number">{rowNumber}</td>
                      <td>
                        <div className="user-name-cell">
                          <div className="user-avatar-small">
                            {(user.first_name || user.name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <div className="user-name-info">
                            <div className="user-name">{user.first_name || user.name || 'N/A'}</div>
                            {user.avatar_details && Array.isArray(user.avatar_details) && user.avatar_details.length > 0 && (
                              <div className="user-avatar-count">
                                {user.avatar_details.length} avatar{user.avatar_details.length > 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="user-email">{user.email || 'N/A'}</td>
                      <td>
                        {user.plan_details && user.plan_details.current && user.plan_details.current.plan ? (
                          <span className="plan-badge">
                            {user.plan_details.current.plan.name || 'N/A'}
                          </span>
                        ) : (
                          <span className="plan-badge no-plan">No Plan</span>
                        )}
                      </td>
                      <td>
                        <div
                          className={`status-indicator ${isActive ? 'positive' : 'negative'} clickable`}
                          onClick={() => handleStatusToggle(user.id || user.user_id, displayStatus)}
                          title={`Click to change status from ${displayStatus} to ${isActive ? 'deactive' : 'active'}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <span className="status-arrow">
                            {isActive ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="18 15 12 9 6 15"></polyline>
                              </svg>
                            ) : (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            )}
                          </span>
                          <span className="status-text">{displayStatus.toUpperCase()}</span>
                        </div>
                      </td>
                      <td className="date-cell">{formatDate(user.created_at || user.date_created)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEdit(user)}
                            title="Edit User"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                          <button
                            className="action-btn view-btn"
                            onClick={() => handleView(user)}
                            title="View User"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDelete(user.id || user.user_id)}
                            title="Delete User"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <UserModal
            user={editingUser}
            onClose={handleModalClose}
          />
        )}

        {showViewModal && (
          <UserViewModal
            user={viewingUser}
            onClose={handleViewModalClose}
          />
        )}

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
          confirmButtonType={confirmModal.confirmButtonType}
        />

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="table-pagination">
            <div className="pagination-left">
              <select
                className="page-size-select"
                value={pageSize}
                onChange={(e) => {
                  // Note: pageSize is currently fixed, but this sets up the UI
                  console.log('Page size changed to:', e.target.value);
                }}
              >
                <option value="10">10 / page</option>
                <option value="15">15 / page</option>
                <option value="20">20 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
              </select>
            </div>

            <div className="pagination-right">
              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              <div className="pagination-numbers">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(1, endPage - maxVisible + 1);
                  }

                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        className="pagination-number"
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`pagination-number ${i === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(i)}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        className="pagination-number"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;



